import React, { useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import Moment from 'moment';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { TASK_COMPLETION_SLICES, HEATMAP_VIEWS, DATE_FORMAT, FONT_FAMILY } from '../../../constants/constants';
import Notification from '../../Common/Notification';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import ResourceAllocationTasksDrawer from './ResourceAllocationTasksDrawer';
import Button from '../../Common/Button';
import ExportButton from './ExportButton/ExportButton';
import {
    useGetResourceAllocations,
    useGetResourceAllocationsDetails,
} from '../../../apis/locationTasks';
import type {
    HeatmapSeries,
    TaskSearchRequest,
} from '../../../apis/locationTasks/locationTask.types';
import './ResourceAllocationView.scss';

interface ResourceAllocationViewProps {
    settings: { isProjectWidgetActive: boolean };
    locations: Array<{ id: string }>;
    searchParams: TaskSearchRequest;
    lastSearchTime?: Date;
    showTask: (id: number) => void;
}

interface ClickedCell {
    seriesName: string;
    targetDateStart: string;
    targetDateEnd: string;
}

const buildChartOptions = (onCellClick: (event: unknown, chartContext: unknown, config: any) => void) => ({
    stroke: { width: 0 },
    yaxis: {
        minHeight: 30,
        maxHeight: 30,
        reversed: true,
        labels: { style: { fontFamily: FONT_FAMILY } },
        axisBorder: { show: true, color: '#8b9095' },
        axisTicks: { show: false },
    },
    xaxis: {
        minHeight: 30,
        maxHeight: 30,
        labels: { style: { fontFamily: FONT_FAMILY } },
        axisBorder: { show: true, color: '#8b9095' },
        axisTicks: { show: false },
    },
    grid: { show: false },
    chart: {
        events: { click: onCellClick },
        toolbar: { show: false },
        animations: {
            enabled: true,
            speed: 250,
            animateGradually: { enabled: true, delay: 100 },
            dynamicAnimation: { enabled: true, speed: 100 },
        },
    },
    plotOptions: {
        heatmap: {
            distributed: true,
            stroke: { width: 1 },
        },
    },
    colors: ['#00a4fb'],
    dataLabels: {
        style: {
            colors: ['#666'],
            fontFamily: FONT_FAMILY,
        },
        formatter: (val: number) => (val == 0 ? '' : val),
    },
});

export function ResourceAllocationView({
    settings,
    locations,
    searchParams,
    showTask,
}: ResourceAllocationViewProps) {
    // if projects enabled it should be the default selection
    const [selectedDivider, setSelectedDivider] = useState<string>(
        settings.isProjectWidgetActive ? TASK_COMPLETION_SLICES.Project : TASK_COMPLETION_SLICES.Discipline
    );
    const [selectedView, setSelectedView] = useState<string>('month');
    const [pageNumber, setPageNumber] = useState(1);
    const [clickedCell, setClickedCell] = useState<ClickedCell | null>(null);
    const [showTasksDrawer, setShowTasksDrawer] = useState(false);

    const chartRef = useRef<any>(null);

    const locationKeys = useMemo(() => locations.map((l) => l.id), [locations]);

    // ── Heatmap data ─────────────────────────────────────────────────────────
    // since we don't know the start of the first task or the end, we have to depend on the API for that logic
    // the api is smart enough to figure out the date range based on the pageNumber and selectedView
    const allocationsRequest = useMemo(
        () => ({
            ...searchParams,
            locationKeys,
            divider: selectedDivider,
            view: selectedView,
            pageNumber,
        }),
        [searchParams, locationKeys, selectedDivider, selectedView, pageNumber]
    );

    const {
        data: allocationsData,
        isPending: isLoading,
        isError,
        error,
    } = useGetResourceAllocations(allocationsRequest, { enabled: locationKeys.length > 0 });

    useEffect(() => {
        if (isError) Notification('error', 'Error', (error as Error)?.message ?? 'Failed to load resource allocations');
    }, [isError, error]);

    const series: HeatmapSeries[] | null = allocationsData?.seriesData ?? null;
    const isLastPage = allocationsData?.isLastPage ?? true;
    const startDateRange = allocationsData?.startDateRange ?? Moment().toISOString();
    const endDateRange = allocationsData?.endDateRange ?? Moment().toISOString();

    // ── Drill-down details for the clicked heatmap cell ─────────────────────
    const detailsRequest = useMemo(
        () => ({
            ...searchParams,
            locationKeys,
            divider: selectedDivider,
            view: selectedView,
            seriesName: clickedCell?.seriesName ?? '',
            targetDateStart: clickedCell?.targetDateStart ?? null,
            targetDateEnd: clickedCell?.targetDateEnd ?? null,
        }),
        [searchParams, locationKeys, selectedDivider, selectedView, clickedCell]
    );

    const {
        data: detailsData,
        isError: isDetailsError,
        error: detailsError,
    } = useGetResourceAllocationsDetails(detailsRequest, { enabled: !!clickedCell });

    useEffect(() => {
        if (detailsData && clickedCell) setShowTasksDrawer(true);
    }, [detailsData, clickedCell]);

    useEffect(() => {
        if (isDetailsError) Notification('error', 'Error', (detailsError as Error)?.message ?? 'Failed to load tasks');
    }, [isDetailsError, detailsError]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const onDropDownChange = (e: DropdownChangeEvent) => {
        setSelectedDivider(e.value);
        setPageNumber(1);
    };

    const onChangeView = (view: string) => {
        setSelectedView(view);
        setPageNumber(1);
    };

    const onNavPrev = () => {
        if (pageNumber > 1 && selectedView !== HEATMAP_VIEWS.Month.value) {
            setPageNumber((prev) => prev - 1);
        }
    };

    const onNavNext = () => {
        if (selectedView !== HEATMAP_VIEWS.Month.value) {
            setPageNumber((prev) => prev + 1);
        }
    };

    const getRangeDisplay = () =>
        Moment(startDateRange).add(1, 'day').format(DATE_FORMAT) + ' - ' + Moment(endDateRange).format(DATE_FORMAT);

    const onHeatmapClick = (event: unknown, chartContext: unknown, config: any) => {
        // figure out date range, make api call to get task info, open drawer
        if (config.seriesIndex != -1 && config.dataPointIndex != -1 && series) {
            const seriesName = series[config.seriesIndex].name ?? '';
            // Derive the bucket's date range from the API start date plus the cell
            // index. The x-axis labels are localized by the server (e.g. "set. 2019"
            // in pt-BR), so parsing them with a fixed-locale Moment format breaks.
            const unit =
                selectedView === HEATMAP_VIEWS.Month.value ? 'month'
                : selectedView === HEATMAP_VIEWS.Week.value ? 'week'
                : 'day';
            const start = Moment(startDateRange).add(config.dataPointIndex, unit);
            const end = Moment(start).add(1, unit);
            if (start.isValid() && end.isValid()) {
                setClickedCell({
                    seriesName,
                    targetDateStart: start.format('YYYY-MM-DD'),
                    targetDateEnd: end.format('YYYY-MM-DD'),
                });
            }
        }
    };

    const onCloseTasksDrawer = () => {
        setShowTasksDrawer(false);
        setClickedCell(null);
    };

    const chartOptions = useMemo(() => buildChartOptions(onHeatmapClick), [series, selectedView]);

    const getHeight = () => {
        const seriesCount = Array.isArray(series) ? series.length : 0;
        const calculatedHeight = 100 + seriesCount * 30;
        return isNaN(calculatedHeight) ? 100 : calculatedHeight;
    };

    const handleExport = async (format: string) => {
        if (!chartRef.current) return;
        try {
            if (format === 'png' || format === 'svg') {
                const { imgURI } = await chartRef.current.chart.dataURI();

                // Create a download link for PNG or SVG
                if (format === 'png') {
                    const link = document.createElement('a');
                    link.href = imgURI; // Use the PNG dataURI
                    link.download = 'chart.png'; // Set the file name for PNG
                    link.click(); // Trigger the download
                } else {
                    const chart = chartRef.current.chart;
                    if (chart) {
                        // Get the SVG element from the chart DOM
                        const svgElement = chart.el.querySelector('svg');
                        if (svgElement) {
                            const svgData = new XMLSerializer().serializeToString(svgElement);
                            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
                            const url = URL.createObjectURL(svgBlob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = 'chart.svg'; // Set the file name for SVG
                            document.body.appendChild(link);
                            link.click(); // Trigger the download
                            document.body.removeChild(link); // Clean up
                            URL.revokeObjectURL(url); // Free up memory
                        } else {
                            console.error('SVG element not found.');
                        }
                    } else {
                        console.error('Chart reference is invalid.');
                    }
                }
            } else if (format === 'csv') {
                // Extract the chart data
                const seriesData = series ?? [];
                const csvContent: string[] = [];

                // Create a header row with series names and x-axis label
                const headerRow = ['category', ...seriesData.map((s) => s.name)];
                csvContent.push(headerRow.join(','));

                // Create a map to store y-values for each x value
                const xValues: Record<string, Record<string, unknown>> = {};

                seriesData.forEach((s) => {
                    s.data.forEach((point) => {
                        if (!xValues[point.x as string]) {
                            xValues[point.x as string] = {}; // Initialize if not already present
                        }
                        xValues[point.x as string][s.name] = point.y; // Store the y value under the series name
                    });
                });

                // Populate the CSV rows
                Object.keys(xValues).forEach((xValue) => {
                    const row: unknown[] = [xValue];
                    seriesData.forEach((s) => {
                        row.push(xValues[xValue][s.name] !== undefined ? xValues[xValue][s.name] : ''); // Add y value or empty string
                    });
                    csvContent.push(row.join(','));
                });

                // Convert array to CSV format
                const csvString = `data:text/csv;charset=utf-8,${csvContent.join('\n')}`;
                const link = document.createElement('a');
                link.href = encodeURI(csvString);
                link.download = 'chart.csv'; // Set the file name for CSV
                link.click(); // Trigger the download
            }
        } catch (err) {
            console.error('Export error:', err);
            Notification('error', 'Export error', (err as Error).message);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    const originalOptions = [
        { id: TASK_COMPLETION_SLICES.Discipline, name: TASK_COMPLETION_SLICES.Discipline },
        { id: TASK_COMPLETION_SLICES.Department, name: TASK_COMPLETION_SLICES.Department },
        { id: TASK_COMPLETION_SLICES.Responsible, name: TASK_COMPLETION_SLICES.Responsible },
        { id: TASK_COMPLETION_SLICES.Community, name: TASK_COMPLETION_SLICES.Community },
    ];

    if (settings.isProjectWidgetActive) {
        originalOptions.push({ id: TASK_COMPLETION_SLICES.Project, name: TASK_COMPLETION_SLICES.Project });
    }

    const dividedOptions = originalOptions.map((option) => ({
        label: option.name,
        value: option.id,
    }));

    return (
        <div>
            <LoadingIndicator isLoading={isLoading}>
                <div
                    className='workload-div'
                    style={{
                        overflow: 'hidden',
                        background: '#f8f9fa',
                        marginTop: '20px',
                        paddingLeft: '7px',
                    }}>
                    <div className='header-top-buttons'>
                        <div className='nav-controls'>
                            <Dropdown
                                className='ui-column-filter'
                                value={selectedDivider}
                                options={dividedOptions}
                                onChange={onDropDownChange}
                                style={{ width: '250px' }}
                            />
                            {selectedView !== HEATMAP_VIEWS.Month.value && pageNumber !== 1 && (
                                <Button type='button' iconName='chevron-left' bootstrapClass='light button-prev' onClick={onNavPrev} />
                            )}
                            {selectedView !== HEATMAP_VIEWS.Month.value && !isLastPage && (
                                <Button type='button' iconName='chevron-right' bootstrapClass='light button-next' onClick={onNavNext} />
                            )}
                        </div>

                        <div>
                            <h2>{getRangeDisplay()}</h2>
                        </div>
                        <div>
                            <div className='float-end'>
                                <div className='btn-group'>
                                    <button
                                        type='button'
                                        className={selectedView === HEATMAP_VIEWS.Month.value ? 'btn selected-button' : 'btn btn-light'}
                                        onClick={() => onChangeView(HEATMAP_VIEWS.Month.value)}>
                                        {HEATMAP_VIEWS.Month.label}
                                    </button>
                                    <button
                                        type='button'
                                        className={selectedView === HEATMAP_VIEWS.Week.value ? 'btn selected-button' : 'btn btn-light'}
                                        onClick={() => onChangeView(HEATMAP_VIEWS.Week.value)}>
                                        {HEATMAP_VIEWS.Week.label}
                                    </button>
                                    <button
                                        type='button'
                                        className={selectedView === HEATMAP_VIEWS.Day.value ? 'btn selected-button' : 'btn btn-light'}
                                        onClick={() => onChangeView(HEATMAP_VIEWS.Day.value)}>
                                        {HEATMAP_VIEWS.Day.label}
                                    </button>
                                </div>

                                <ExportButton onExport={handleExport}></ExportButton>
                            </div>
                        </div>
                    </div>
                    <div className='chart-section' style={{ height: getHeight(), background: 'white' }}>
                        {!isLoading && series && (
                            <Chart
                                ref={chartRef}
                                type='heatmap'
                                options={chartOptions as any}
                                series={series as any}
                                width='100%'
                                height='100%'
                                style={{ width: '100%', height: getHeight() }}
                            />
                        )}
                        {!series && <div>No Results Found</div>}
                    </div>
                </div>
            </LoadingIndicator>
            {showTasksDrawer && clickedCell && (
                <ResourceAllocationTasksDrawer
                    tasks={detailsData?.tasks ?? []}
                    seriesName={clickedCell.seriesName}
                    dateRangeStart={Moment(clickedCell.targetDateStart).format(DATE_FORMAT)}
                    dateRangeEnd={Moment(clickedCell.targetDateEnd).format(DATE_FORMAT)}
                    onCancel={onCloseTasksDrawer}
                    showTask={showTask}
                />
            )}
        </div>
    );
}

export default ResourceAllocationView;
